import { useCallback, useMemo, useState } from "react";
import { useOverlay } from "../../gui/Overlay/OverlayProvider";
import { usePortFilter } from "../../hooks/PortFilter";
import usePortfolio from "../../hooks/PortfolioProvider";
import { usePath } from "../../libs/path/PathProvider";
import useDateRangeFilter, { DateRangeFilterProvider } from "../../portlibs/DateRangeFilterProvider";
import TransanctionsToolbar from "./TransactionsToolbar";
import { THoldingTypes, getHoldingType } from "../../shared/Consts";
import Utils from "../../libs/Utils";
import PortUtils from "../../portlibs/PortUtils";
import DialogTitle from "../widgets/DialogTitle";
import TransactionTable from "../widgets/TransactionTable";
import { Price } from "../../models/Price";
import { Transaction } from "../../models/Transaction";

const DIALOG_ID = "transactions"
const COLS : Record<THoldingTypes,string[]> = {
    cash:["date", "accountId", "quantity", "type", "balance", "note"],
    equity:["date", "accountId", "quantity", "price", "type", "note"],
    fixed:["date", "accountId", "quantity", "yield", "maturity", "note"],
    save:["date", "accountId", "quantity", "yield", "note"]
}
const SORT_RESOLVER = ["date","id","accountId","type","quantity"]

function TransactionsDlg(props:{holdingId:string}) {
    const cols = COLS[getHoldingType(props.holdingId)]
    const [search,setSearch] = useState<string>("")
    const {portfolio} = usePortfolio()
    const {account} = usePortFilter()
    const {start,end} = useDateRangeFilter()
    const holding = portfolio.holding(props.holdingId)!
    const transactions = useMemo(() => {
        var list = account>=0 ? holding.filterByAccount(account).transactions : holding.transactions
        list = search ? list.filter(trans => trans.filter(search)) : list
        list = list.filter(trans => trans.date>=start && trans.date<=end)
        return list
    }, [account, search])
    const balance = useMemo(() => {
        if (!Price.isCash(props.holdingId)) return {}
        transactions.sort((l:Transaction,r:Transaction) => {
            var left = l.date.getTime(), right = r.date.getTime()
            if (left!=right) return left-right
            else if (l.accountId!=r.accountId) return l.accountId-r.accountId
            else if (l.type!=r.type) return l.type-r.type
            else return r.quantity-l.quantity
        })
        var total = 0
        const balance : Record<string,number> = {}
        for(const row of transactions) {
            total += row.price*row.quantity
            balance[row._id] = total
        }
        return balance
    }, [transactions])

    const onExport = useCallback(() => Utils.exportFile(PortUtils.toCSV(transactions, cols), `Trans ${Utils.formatDate("yyyy-MM-dd", new Date())}.csv`), [transactions])
    return <>
        <DialogTitle title={holding.name} overlayId={DIALOG_ID}/>
        <TransanctionsToolbar search={search} onSearch={search=>setSearch(search)} onExport={onExport}/>
        <div className="flex1 content scroller">
            <TransactionTable holding={holding} tableName="transactions" cols={cols} rows={transactions} sortResolver={SORT_RESOLVER} balance={balance}/>
        </div>
    </>
}
export default function useTransactionsDlg(){
    const {setHash} = usePath()
    const {addOverlay,hasOverlay} = useOverlay()
    const {portfolio} = usePortfolio()
    const afterClosed = () => setHash("")
    const showHistory = (id:string) => {
        const holding = portfolio.holding(id)
        if (hasOverlay(DIALOG_ID) || !holding) return
        addOverlay(DIALOG_ID, {
            children:<DateRangeFilterProvider><TransactionsDlg holdingId={id}/></DateRangeFilterProvider>,
            backdropClass:"fullscreen",
            className:"flex vert",
            afterClosed
        })
    }

    return {showHistory}
}